<script lang="ts">
	import type { FormBlock } from '$models/Block/FormBlock';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { createCmsFormState } from '$lib/state/cms-form.svelte';
	import { sanitizeHtml } from '$lib/utils';

	let { block }: { block: FormBlock } = $props();

	const allowedInputFields = new Set(['text', 'email', 'phone', 'url', 'number']);
	const allowedFields = new Set([
		'textarea',
		'select',
		'radio',
		'checkbox',
		'captcha',
		...allowedInputFields
	]);
	const formState = createCmsFormState();
	const captchaField = $derived(block.form.fields.find((field) => field.type === 'captcha'));
	const hasCaptcha = $derived(
		captchaField?.captcha?.provider === 'recaptcha' && Boolean(captchaField.captcha.siteKey)
	);

	function getInputType(fieldType: string) {
		return fieldType === 'phone' ? 'tel' : fieldType;
	}

	async function handleSubmit(event: SubmitEvent) {
		event.preventDefault();
		const success = await formState.submit(
			event.currentTarget as HTMLFormElement,
			block.errorMessage
		);

		if (!success && hasCaptcha) {
			(window as Window & { grecaptcha?: { reset: () => void } }).grecaptcha?.reset();
		}
	}

	// Google's reCAPTCHA script auto-renders any `.g-recaptcha` element present
	// when its script loads, so we only need to load the script once per page.
	$effect(() => {
		if (!hasCaptcha || document.getElementById('recaptcha-api-script')) {
			return;
		}

		const script = document.createElement('script');
		script.id = 'recaptcha-api-script';
		script.src = 'https://www.google.com/recaptcha/api.js';
		script.async = true;
		script.defer = true;
		document.head.appendChild(script);
	});
</script>

<div class="block-main">
	{#if formState.success()}
		<!-- TODO: Add redirect and page cases? -->
		{#if block.form.successAction.type === 'message'}
			<div class="message message--success">
				{@html sanitizeHtml(block.form.successAction.message ?? '')}
			</div>
		{/if}
	{:else}
		<div class="form-wrapper">
			<div class="form-text rich-text">
				{@html sanitizeHtml(block.text ?? '')}
			</div>
			<div class="form-main">
				<form onsubmit={handleSubmit} class={['form', formState.submitting() && 'is-loading']}>
					<!-- Generic error message when nothing better is available -->
					{#if formState.errorMessage() !== undefined}
						<div class="form-row">
							<div class="message message--error">
								{@html sanitizeHtml(formState.errorMessage() ?? '')}
							</div>
						</div>
					{/if}

					<!-- Render fields -->
					{#each block.form.fields as field (field.id)}
						{#if allowedFields.has(field.type)}
							{@const validationError = formState.error(field.id)}

							<div class={['form-row', field.classes]}>
								{#if field.type !== 'captcha'}
									<label class="form-label" for={field.id}>
										{field.name}
										{#if field.required}
											<span>*</span>
										{/if}
									</label>
								{/if}

								{#if allowedInputFields.has(field.type)}
									<!-- Generic input types -->
									<input
										id={field.id}
										type={getInputType(field.type)}
										name={field.id}
										required={field.required}
									/>
								{:else if field.type === 'textarea'}
									<!-- Textarea -->
									<textarea
										id={field.id}
										name={field.id}
										cols="30"
										rows="10"
										required={field.required}
									></textarea>
								{:else if field.type === 'select'}
									<!-- Select -->
									<select name={field.id} id={field.id} required={field.required}>
										{#each field.options as option, index (`${option.value}-${index}`)}
											<option value={option.value}>
												{option.label}
											</option>
										{/each}
									</select>
								{:else if field.type === 'radio'}
									<!-- Radio -->
									<div class="form-radio-group">
										{#each field.options as option, index (`${option.value}-${index}`)}
											<div class="form-radio">
												<label for={`${field.id}-${index + 1}`}>
													<input
														type="radio"
														name={field.id}
														value={option.value}
														required={field.required}
														id={`${field.id}-${index + 1}`}
													/>
													<span><!-- input --></span>
													{option.label}
												</label>
											</div>
										{/each}
									</div>
								{:else if field.type === 'checkbox'}
									<!-- Checkbox -->
									<div class="form-checkbox-group">
										<!-- @TODO: Fix multiple selections submission -->
										{#each field.options as option, index (`${option.value}-${index}`)}
											<div class="form-checkbox">
												<label for={`${field.id}-${index + 1}`}>
													<input
														type="checkbox"
														name={field.id}
														value={option.value}
														required={field.required}
														id={`${field.id}-${index + 1}`}
													/>
													<span><!-- input --></span>
													{option.label}
												</label>
											</div>
										{/each}
									</div>
								{:else if field.type === 'captcha' && field.captcha?.provider === 'recaptcha'}
									<!-- reCAPTCHA -->
									<div
										class="g-recaptcha"
										data-sitekey={field.captcha.siteKey}
										data-size={field.captcha.size}
										data-theme={field.captcha.theme}
									></div>
								{/if}

								{#if validationError.length > 0}
									<div class="form-error">
										{validationError}
									</div>
								{/if}
							</div>
						{/if}
					{/each}

					<input type="hidden" name="form_id" value={block.form.id} />

					<div class="form-submit">
						<button type="submit" class="button" disabled={formState.submitting()}>
							{block.form.submitLabel}
						</button>
					</div>
				</form>
			</div>
			{#if block.image}
				<div class="form-image">
					<img
						src={block.image.url}
						srcset={getWordPressImageSrcSet(block.image)}
						sizes="(min-width: 768px) 300px, 80vw"
						alt={block.image.alt}
						width={block.image.width}
						height={block.image.height}
						loading="lazy"
						decoding="async"
					/>
				</div>
			{/if}
		</div>
	{/if}
</div>

<style lang="postcss">
	.form {
		&-wrapper {
			max-width: var(--container-extra-small);
			margin: 0 auto;

			@media (--md) {
				display: flex;
				flex-wrap: wrap;
				align-items: flex-start;
			}
		}

		&-text {
			width: 100%;
		}

		&-main {
			flex: 1;

			&:not(:last-child) {
				margin-bottom: var(--spacing-8);

				@media (--md) {
					margin-bottom: 0;
				}
			}

			.form {
				width: 100%;
			}
		}

		&-text {
			margin-bottom: var(--spacing-8);
		}

		&-image {
			max-width: 300px;

			@media (--md) {
				margin-left: var(--spacing-12);
			}
		}
	}
</style>
